import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject, Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';

import { CustomerService } from '../services/customer.service';
import { ApplicationStateService } from '../services/application-state.service';
import { LoadingSpinnerService } from '../services/loading-spinner.service';
import { PageTitleService } from '../services/page-title.service';
import { QuoteInfoService } from '../services/quote-info.service';
import { CurrentUserService } from '../services/current-user.service';
import { ModalDialogService } from '../reusable-components/modal-dialog/modal-dialog.service';
import { QuoteService } from '../services/quote.service';
import { DataService } from '../services/data.service';
import { HttpResponseService } from '../services/httpresponse-service';
import {
  ToasterService,
  Toast,
  ToasterConfig,
  BodyOutputType
} from 'angular2-toaster';
import { NavigationService } from '../services/navigation.service';

import { Customer } from '../model/customer';
import { Quote } from '../model/quote';
import { Constants, TextValuePair } from '../model/constants';
import { BrowserWidthService } from '../services/browser-width.service';
import { ProspectsService } from '../services/prospect.service';
import { Prospect } from '../model/prospect';
import { QuoteRevision } from '../model/quote-revision';

@Component({
  selector: 'my-customer-info',
  template: require('./customer-info.component.html'),
  styles: [require('./customer-info.component.scss')]
})
export class CustomerInfoComponent implements OnInit, OnDestroy {
  environment: string;
  states: TextValuePair[];
  displayMode: string;
  stateError: string = '';
  deactivateMessage: string = '';
  currentUserId: number;
  modifiedBy: string;
  currentRetailerNumber: string;

  requireZipValidation: Subject<boolean> = new Subject<boolean>();
  requireEmailValidation: Subject<boolean> = new Subject<boolean>();
  requirePhoneValidation: Subject<boolean> = new Subject<boolean>();
  processing: Subject<boolean> = new BehaviorSubject(false);

  customer = new Customer();
  appState = this.applicationStateService.getApplicationState();
  quote: Quote;
  timeStamp = new Date();

  stockHome: boolean;
  submitted = false;
  stateIsInvalid: boolean = false;
  editInProcess: boolean = false;
  canNavigate: boolean = false;
  quoteId: number;
  showingDeactivateModal: boolean = false;
  phoneError: boolean = false;
  lastNameRequiredError: boolean = false;
  firstNameRequiredError: boolean = false;

  public phoneMask = [
    '(',
    /[1-9]/,
    /\d/,
    /\d/,
    ')',
    ' ',
    /\d/,
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    /\d/,
    /\d/
  ];

  subscription: Subscription = new Subscription();

  customerTab: boolean = true;
  prospectsTab: boolean = false;
  displayNoMatches: boolean = true;
  prospects: Prospect[];
  prospectSelected: boolean = false;
  isBusy: boolean = false;
  searchValue: string;
  searchPipeValue: string;
  searchTerm: string;
  filteringProperties: Array<string> = [];
  searchFirstName: string = null;
  searchLastName: string = null;
  baseCustomerTitle: string = 'Customer Information';
  panelCustomerTitle: string;
  tabName: string = 'Customer';

  config: ToasterConfig = new ToasterConfig({
    positionClass: 'toast-bottom-right'
  });

  tabObjects = [
    {
      tabName: 'Customer',
      tabTitle: 'Customer',
      isDefault: true,
      sortSequence: 1,
      isActive: true
    },
    {
      tabName: 'Prospects',
      tabTitle: 'Prospects',
      isDefault: false,
      sortSequence: 2,
      isActive: false
    }
  ];

  constructor(
    public router: Router,
    public customerService: CustomerService,
    public applicationStateService: ApplicationStateService,
    public route: ActivatedRoute,
    public spinner: LoadingSpinnerService,
    public pageTitleService: PageTitleService,
    public quoteInfoService: QuoteInfoService,
    public currentUserService: CurrentUserService,
    public modalDialogService: ModalDialogService,
    public quoteService: QuoteService,
    public browserWidthService: BrowserWidthService,
    public toasterService: ToasterService,
    public prospectsService: ProspectsService,
    public dataService: DataService,
    public httpResponseService: HttpResponseService,
    public navigationService: NavigationService
  ) {
    const currentUserSub = this.currentUserService.currentUser$.subscribe(x => {
      this.modifiedBy = x.cmhUserId + '-' + x.firstName + ' ' + x.lastName;
      this.currentUserId = x.cmhUserId;
      this.currentRetailerNumber = x.retailerNumber;
      this.applicationStateService.setCmhUser(x);
    });
    this.subscription.add(currentUserSub);
    const routeParamsSub = this.route.params.subscribe(x => {
      this.quoteId = x['quoteId'];
      this.quoteInfoService.quoteId$.next(this.quoteId);
    });
    this.subscription.add(routeParamsSub);

    this.pageTitleService.pageTitle.next({
      title: Constants.CmhConnectionTitle
    });
    this.panelCustomerTitle = this.baseCustomerTitle;
  }

  ngOnInit() {
    this.spinner.showSpinner();
    this.stateIsInvalid = false;
    this.quote = new Quote();
    this.quote.userId = this.currentUserId;
    this.quote.retailerNumber = this.currentRetailerNumber;
    if (this.route.snapshot.params['quoteId'] === 'new-quote') {
      this.quote.quoteId = 0;
    } else {
      this.quote.quoteId = +this.route.snapshot.params['quoteId'];
    }

    const canAccessQuoteSub = this.quoteInfoService.canAccessQuote$.subscribe(
      canAccess => {
        if (canAccess) {
          if (this.quote.quoteId > 0) {
            const refreshStateFromDB = this.applicationStateService
              .refreshStateFromDB(this.quote.quoteId)
              .subscribe(
                dbQuote => {
                  if (dbQuote !== undefined && dbQuote !== null) {
                    this.pageTitleService.pageTitle.next({
                      title: Constants.CmhConnectionTitle,
                      subTitle:
                        Constants.CurrentQuoteSubTitle +
                        (dbQuote.iseriesQuoteNumber > 0 &&
                        dbQuote.QuoteStatusesID > 0
                          ? dbQuote.iseriesQuoteNumber
                          : this.quoteId)
                    });
                    this.applicationStateService.setQuote(dbQuote);
                    this.quote = dbQuote;
                    this.quoteInfoService.quote$.next(dbQuote);
                    this.quoteInfoService.quote$.next(this.quote);
                    this.initializeCustomer();
                  } else {
                    this.spinner.hideSpinner();
                    this.stateIsInvalid = true;
                    this.modalDialogService
                      .showMessage({
                        headerText: 'Customer Information',
                        message: Constants.QuoteNotFoundMessage.replace(
                          '#quoteid#',
                          this.quoteId.toString()
                        )
                      })
                      .subscribe(messageReply => {
                        this.redirectRequired();
                      });
                  }
                },
                error => {
                  this.spinner.hideSpinner();
                  this.httpResponseService.httpErrorResponse$.next(error);
                }
              );
            this.subscription.add(refreshStateFromDB);
          } else {
            this.quote.DateCreated = this.timeStamp;
            this.quote.productLineId = 0;
            this.quote.QuoteStatusesID = 0;
            this.quote.ApprovalStatusDescription = 'Not Submitted';
            this.appState.quote = this.quote;
            this.initializeCustomer();
            this.quoteInfoService.quote$.next(this.quote);
            this.spinner.hideSpinner();
          }
        }
      }
    );
    this.subscription.add(canAccessQuoteSub);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (!this.prospectSelected) {
      this.customerService.updateCustomer(this.customer);
    }
  }

  checkForClaytonRetailer() {
    return Array.isArray(this.currentRetailerNumber.match(/[0]$/));
  }

  initializeCustomer() {
    this.stockHome = false;
    this.states = Constants.States;
    this.processing.next(false);

    const requireZipSub = this.requireZipValidation.subscribe(x => {});
    this.subscription.add(requireZipSub);
    const requireEmailSub = this.requireEmailValidation.subscribe(x => {});
    this.subscription.add(requireEmailSub);
    const requirePhoneSub = this.requirePhoneValidation.subscribe(x => {});
    this.subscription.add(requirePhoneSub);

    this.timeStamp = new Date();

    if (this.appState.quote && this.appState.quote.quoteId === 0) {
      this.customer = new Customer();
      this.customer.customerId = 0;
      this.spinner.hideSpinner();
    } else {
      if (this.quote.customerId > 0) {
        this.tabName = 'Customer';
        const getCustomerById = this.customerService
          .getCustomerById(this.quote.customerId)
          .subscribe(
            dbCustomer => {
              if (dbCustomer !== undefined && dbCustomer !== null) {
                this.customer = dbCustomer;
                this.quote.customerId = this.customer.customerId;
                this.applicationStateService
                  .setCustomer(dbCustomer)
                  .subscribe();
                this.quoteInfoService.customerId$.next(dbCustomer.customerId);
                this.customer = dbCustomer;
                this.stockHome =
                  this.customer.firstName.toLocaleLowerCase() === 'stock'
                    ? true
                    : false;

                this.applicationStateService.setQuote(this.quote);
                this.quoteInfoService.customer$.next(this.customer);
              } else {
                this.stateIsInvalid = true;
                this.modalDialogService
                  .showMessage({
                    headerText: 'Customer Information',
                    message:
                      'Specified customer (' +
                      this.appState.quote.customerId +
                      ') could not be found. ' +
                      'You will be redirected create a new quote.'
                  })
                  .subscribe(messageReply => {
                    this.redirectRequired();
                  });
              }
            },
            error => {
              this.spinner.hideSpinner();
              this.httpResponseService.httpErrorResponse$.next(error);
            },
            () => {
              this.spinner.hideSpinner();
            }
          );
        this.subscription.add(getCustomerById);
      } else {
        if (this.quote.prospectId > 0) {
          const getProspectById = this.prospectsService
            .getProspectById(this.currentRetailerNumber, this.quote.prospectId)
            .subscribe(
              prospect => {
                if (prospect !== undefined) {
                  this.prospects = new Array<Prospect>();
                  this.prospects.push(prospect);
                  this.displayNoMatches =
                    this.prospects === undefined || this.prospects.length === 0
                      ? true
                      : false;
                  this.dataService.indexResults(
                    this.prospects,
                    this.filteringProperties
                  );
                  this.panelCustomerTitle =
                    this.baseCustomerTitle + ' - ' + prospect.Contact.FullName;
                  this.prospectsTab = true;
                  this.customerTab = false;
                  this.tabName = 'Prospects';
                  this.prospectSelected = true;
                  this.applicationStateService.setProspect(
                    prospect,
                    this.modifiedBy
                  );
                }
              },
              customerError => {
                this.spinner.hideSpinner();
                this.stateIsInvalid = true;
                this.modalDialogService
                  .showMessage({
                    headerText: 'Customer Information',
                    message:
                      'There was an error retreiving prospect (' +
                      this.quote.prospectId +
                      ').' +
                      'You will be redirected to the quote selection page.'
                  })
                  .subscribe(messageReply => {
                    this.redirectRequired();
                  });
              },
              () => {
                this.spinner.hideSpinner();
              }
            );
          this.subscription.add(getProspectById);
        }
      }
    }
  }

  onSubmit(customerForm) {
    this.firstNameRequiredError = false;
    this.lastNameRequiredError = false;
    this.stockHome =
      this.stockHome === null || this.stockHome === undefined
        ? false
        : this.stockHome;
    this.customer.firstName =
      this.customer.firstName === undefined ? null : this.customer.firstName;
    this.customer.lastName =
      this.customer.lastName === undefined ? null : this.customer.lastName;

    if (customerForm !== undefined) {
      if (customerForm.valid) {
        this.submitCustomerInfo();
      } else {
        if (
          !this.stockHome &&
          (this.customer.firstName === null ||
            this.customer.firstName.length === 0)
        ) {
          this.firstNameRequiredError = true;
        }
        if (
          !this.stockHome &&
          (this.customer.lastName === null ||
            this.customer.lastName.length === 0)
        ) {
          this.lastNameRequiredError = true;
        }
      }
    } else {
      if (
        !this.stockHome &&
        (this.customer.firstName === null ||
          this.customer.firstName.length === 0)
      ) {
        this.firstNameRequiredError = true;
      }
      if (
        !this.stockHome &&
        (this.customer.lastName === null || this.customer.lastName.length === 0)
      ) {
        this.lastNameRequiredError = true;
      }
    }
  }

  submitFromEnter(customerForm) {
    this.spinner.showSpinner();
    this.firstNameRequiredError = false;
    this.lastNameRequiredError = false;
    this.customer.firstName =
      this.customer.firstName === undefined ? null : this.customer.firstName;
    this.customer.lastName =
      this.customer.lastName === undefined ? null : this.customer.lastName;
    if (customerForm !== undefined) {
      if (customerForm.valid) {
        this.submitCustomerInfo();
      } else {
        if (
          !this.stockHome &&
          (this.customer.firstName === null ||
            this.customer.firstName.length === 0)
        ) {
          this.firstNameRequiredError = true;
        }
        if (
          !this.stockHome &&
          (this.customer.lastName === null ||
            this.customer.lastName.length === 0)
        ) {
          this.lastNameRequiredError = true;
        }
        this.spinner.hideSpinner();
      }
    }
  }

  get customerView() {
    let customerView: number;
    const currentPricingViewSub = this.quoteInfoService.currentPricingView$.subscribe(
      x => {
        customerView = x;
      }
    );
    this.subscription.add(currentPricingViewSub);
    return customerView;
  }

  onEmailBlurEvent() {
    this.requireEmailValidation.next(true);
  }

  onEmailFocusEvent() {
    this.requireEmailValidation.next(false);
  }

  onPhoneBlurEvent() {
    this.phoneError = false;
    let phone = this.customer.phone.replace(/\D/g, '');
    if (this.customer.phone !== undefined && phone.length < 10) {
      this.customer.phone = undefined;
    }
    this.requirePhoneValidation.next(true);
  }

  onPhoneFocusEvent() {
    this.requirePhoneValidation.next(false);
  }

  onZipFocusEvent() {
    this.requireZipValidation.next(true);
  }

  onZipBlurEvent() {
    this.requireZipValidation.next(true);
  }

  stockHomeChanged() {
    if (this.stockHome) {
      this.customer.firstName = 'Stock';
      this.customer.lastName = 'Home';
      this.lastNameRequiredError = false;
      this.firstNameRequiredError = false;
    } else {
      this.customer.firstName = '';
      this.customer.lastName = '';
    }
    this.customer.email = '';
    this.customer.phone = '';
    this.customer.address = '';
    this.customer.city = '';
    this.customer.state = '';
    this.customer.zip = '';
    this.quoteInfoService.customer$.next(this.customer);
  }

  submitCustomerInfo() {
    this.spinner.showSpinner();
    this.processing.next(true);
    this.submitted = true;
    this.customer.modifiedBy = this.modifiedBy;
    this.applicationStateService.getQuote().modifiedBy = this.modifiedBy;
    let quote = this.applicationStateService.getQuote();
    this.customer.quoteRevisionNumber = quote.revisionNumber;
    if (
      quote.customerId === undefined ||
      quote.customerId === null ||
      quote.customerId === 0
    ) {
      this.customerService.insertCustomer(this.customer).subscribe(
        customer => {
          this.customer.customerId = customer.customerId;
          this.saveCustomer(customer);
        },
        error => {
          this.spinner.hideSpinner();
          this.httpResponseService.httpErrorResponse$.next(error);
        }
      );
    } else {
      this.customer.modifiedBy = this.modifiedBy;
      this.customerService.updateCustomer(this.customer).subscribe(customer => {
        this.saveCustomer(customer);
      });
    }
  }

  saveCustomer(cust: Customer) {
    this.applicationStateService.setCustomer(cust).subscribe(
      quoteResponse => {
        this.quoteInfoService.customerId$.next(quoteResponse.customerId);
        this.quoteInfoService.quoteId$.next(quoteResponse.quoteId);
        this.quoteInfoService.customer$.next(cust);
        this.router.navigate(['choose-product-line', quoteResponse.quoteId]);
      },
      error => {
        this.spinner.hideSpinner();
        this.httpResponseService.httpErrorResponse$.next(error);
      }
    );
  }

  redirectRequired() {
    this.stateIsInvalid = false;
    this.canNavigate = true;
    this.quote.DateCreated = this.timeStamp;
    this.quote.productLineId = 0;
    this.appState.quote = this.quote;
    this.quote.quoteId = 0;
    this.router.navigate([Constants.NewQuotesUrl]);
  }

  canDeactivate(): boolean {
    let canLeave = true;
    if (
      this.quoteInfoService.canAccessQuote$.value === true &&
      !this.stateIsInvalid
    ) {
      if (!this.stockHome && !this.prospectSelected) {
        if (
          ((this.customer.firstName === undefined ||
            this.customer.firstName === null ||
            this.customer.firstName.length === 0) &&
            !this.canNavigate) ||
          ((this.customer.lastName === undefined ||
            this.customer.lastName === null ||
            this.customer.lastName.length === 0) &&
            !this.canNavigate)
        ) {
          if (!this.showingDeactivateModal) {
            this.showingDeactivateModal = true;
            this.modalDialogService
              .showMessage({
                headerText: 'Customer Information',
                message:
                  'You must complete editing Customer Information before leaving page.'
              })
              .subscribe(messageReply => {
                this.showingDeactivateModal = false;
              });
          }
          canLeave = false;
        }
      }
    }
    return canLeave;
  }

  resetValidation() {
    this.firstNameRequiredError = false;
    this.lastNameRequiredError = false;
    this.stockHome = false;
  }

  validateCustomerName() {
    this.customer.lastName =
      this.customer.lastName === undefined || this.customer.lastName === null
        ? null
        : this.customer.lastName;
    this.customer.firstName =
      this.customer.firstName === undefined || this.customer.firstName === null
        ? null
        : this.customer.firstName;

    if (this.customer.lastName !== null) {
      this.lastNameRequiredError = false;
    } else {
      this.lastNameRequiredError = true;
    }
    if (this.customer.firstName !== null) {
      this.firstNameRequiredError = false;
    } else {
      this.firstNameRequiredError = true;
    }
  }

  saveCustomerInfo() {
    this.quoteInfoService.customer$.next(this.customer);
  }

  changeTab(tabName: string) {
    this.tabObjects.forEach(tab => {
      if (tab.tabName === tabName) {
        tab.isActive = true;
      } else {
        tab.isActive = false;
      }
    });

    if (tabName !== undefined && tabName !== null && tabName !== '') {
      if (tabName.toUpperCase() === 'CUSTOMER') {
        this.customerTab = true;
        this.prospectsTab = false;
        this.tabName = 'Customer';
      }
      if (tabName.toUpperCase() === 'PROSPECTS') {
        this.customerTab = false;
        this.prospectsTab = true;
        this.firstNameRequiredError = false;
        this.lastNameRequiredError = false;
        this.tabName = 'Prospects';
      }
    }
  }

  getAddress(prospect: Prospect) {
    var addressString = '';

    if (prospect.Contact.primaryAddress) {
      var address = prospect.Contact.primaryAddress;
      if (address.Address1) {
        addressString = address.Address1;
      }

      if (address.Address2) {
        addressString += ' ' + address.Address2;
      }

      addressString = addressString.trim();

      if (address.City) {
        if (addressString) {
          addressString += '<br /> ' + address.City;
        } else {
          addressString += ' ' + address.City;
        }
      }

      if (address.State) {
        if (addressString) {
          addressString += ', ' + address.State;
        } else {
          addressString += ' ' + address.State;
        }
      }

      addressString = addressString.trim();

      if (address.Zip) {
        addressString += ' ' + address.Zip;
      }
    }

    return addressString;
  }

  selectProspect(prospect: Prospect) {
    this.prospectSelected = true;

    const setProspect = this.applicationStateService
      .setProspect(prospect, this.modifiedBy)
      .subscribe(
        quoteResponse => {
          this.quoteInfoService.prospectId$.next(quoteResponse.prospectId);
          this.quoteInfoService.quoteId$.next(quoteResponse.quoteId);
          this.router.navigateByUrl(
            `/choose-product-line/${quoteResponse.quoteId}`
          );
        },
        error => {
          let toast: Toast = {
            type: 'error',
            title: 'Error has occurred.',
            bodyOutputType: BodyOutputType.TrustedHtml,
            body:
              'Oops! Looks like something went wrong with your request. Please try again or contact <a href="mailto:mfgsupport@claytonhomes.com">MFG Support</a> for assistance'
          };
          this.toasterService.pop(toast);
        },
        () => {}
      );
    this.subscription.add(setProspect);
  }

  searchProspects() {
    this.spinner.showSpinner();
    this.searchFirstName =
      this.searchFirstName === null || this.searchFirstName === ''
        ? ''
        : this.searchFirstName;
    this.searchLastName =
      this.searchLastName === null || this.searchLastName === ''
        ? ''
        : this.searchLastName;
    let searchString = (
      this.searchFirstName +
      ' ' +
      this.searchLastName
    ).trim();
    if (
      searchString !== undefined &&
      searchString !== null &&
      searchString !== ''
    ) {
      this.prospectsService.searchProspects(searchString).subscribe(
        prospects => {
          this.prospects = prospects;
          this.displayNoMatches =
            this.prospects === undefined || this.prospects.length === 0
              ? true
              : false;
          this.dataService.indexResults(prospects, this.filteringProperties);
          this.spinner.hideSpinner();
        },
        error => {
          let toast: Toast = {
            type: 'error',
            title: 'Error has occurred.',
            bodyOutputType: BodyOutputType.TrustedHtml,
            body:
              'Oops! Looks like something went wrong with your request. Please try again or contact <a href="mailto:mfgsupport@claytonhomes.com">MFG Support</a> for assistance'
          };
          this.spinner.hideSpinner();
          this.toasterService.pop(toast);
        },
        () => {
          this.isBusy = false;
        }
      );
    } else {
      this.displayNoMatches = true;
      this.spinner.hideSpinner();
    }
  }

  validateCustomerAndSubmit() {
    let formValid = true;
    this.firstNameRequiredError = false;
    this.lastNameRequiredError = false;
    // standardize data
    this.stockHome =
      this.stockHome === null || this.stockHome === undefined
        ? false
        : this.stockHome;
    this.customer.firstName =
      this.customer.firstName === undefined ? null : this.customer.firstName;
    this.customer.lastName =
      this.customer.lastName === undefined ? null : this.customer.lastName;

    // validate
    if (
      !this.stockHome &&
      (this.customer.firstName === null || this.customer.firstName.length === 0)
    ) {
      this.firstNameRequiredError = true;
      formValid = false;
    }
    if (
      !this.stockHome &&
      (this.customer.lastName === null || this.customer.lastName.length === 0)
    ) {
      this.lastNameRequiredError = true;
      formValid = false;
    }

    if (formValid) {
      this.submitCustomerInfo();
    }
  }

  resetCustomerForm() {
    this.customer.firstName = null;
    this.customer.lastName = null;
    this.customer.email = null;
    this.customer.phone = null;
    this.customer.address = null;
    this.customer.city = null;
    this.customer.state = null;
    this.customer.zip = null;

    this.lastNameRequiredError = false;
    this.firstNameRequiredError = false;
    this.stockHome = false;
  }

  checkForm(form: any): void {
    this.navigationService.hideMchNavigation$.next(form.valid === false);
  }

  /// Sorts the Plants
  sortTabs() {
    if (
      this.tabObjects !== undefined &&
      this.tabObjects !== null &&
      this.tabObjects.length > 1
    ) {
      this.tabObjects.sort((a, b) => {
        return a.sortSequence - b.sortSequence;
      });
    }
  }

  preventChars(event: any) {
    const pattern = /[0-9\+\-\ ]/;
    const pattern2 = /^-?(\d|,)*\.?\d*$/;

    let inputChar = String.fromCharCode(event.charCode);
    if (event.keyCode !== 8 && !pattern2.test(inputChar)) {
      event.preventDefault();
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/customer-info/customer-info.component.ts